import { MdCheck, MdContentCopy, MdOutlineClear, MdOutlineWarningAmber } from 'react-icons/md'

import type { ReactElement, ReactNode } from 'react'

import AppLogo from '~/components/AppLogo'
import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import Section from '~/components/Section'
import Tooltip from '~/components/Tooltip'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useGetApplicationMetadataQuery } from '~/queries/application-metadata'
import { transformByDeviceType } from '~/utils/application'
import { copyText } from '~/utils/navigator'

import './styles.scss'

const DataSection = ({ children, title }: { children: ReactNode; title: string }): ReactElement => {
  return (
    <div className="metadata-section-component">
      <div className="metadata-section-component__title">{title}</div>
      <div>{children}</div>
    </div>
  )
}

const Separator = (): ReactElement => <div className="metadata-separator-component" />

const DataTable = ({ children }: { children: ReactNode }): ReactElement => (
  <table className="metadata-table-component">{children}</table>
)

const CopyCell = ({ valueToCopy }: { valueToCopy: string }): ReactElement => {
  const handleCopy = (value: string) => () => copyText(value, 'Copied to clipboard!')

  return (
    <td className="copy-cell-component">
      <Button ghost onClick={handleCopy(valueToCopy)}>
        <MdContentCopy color="#626262" size={18} />
      </Button>
    </td>
  )
}

const SecretTableCell = ({ hasSecret }: { hasSecret: boolean }): ReactElement => (
  <td className="secret-table-cell">
    {hasSecret ? (
      <MdCheck color="#06703d" size={24} />
    ) : (
      <div className="secret-table-cell__no-secret-container">
        <MdOutlineClear color="#ca3423" size={24} />
        <Tooltip overlay="Missing secret, ask backend team." placement="right" trigger="hover">
          <div>
            <MdOutlineWarningAmber color="#1b1d1f" size={24} />
          </div>
        </Tooltip>
      </div>
    )}
  </td>
)

const AppConfig = (): ReactElement => {
  /** Queries & Mutations */

  const { appDetails, deviceType } = useAppSummary()

  const { data, isLoading } = useGetApplicationMetadataQuery(appDetails?.app_key)

  /** Effects */

  useDocumentTitle('Social login config')

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="app-config-view" withPadding>
        <ViewTitle className="layout-view-title">Social login config</ViewTitle>
        <ContentLayout loading={isLoading}>
          <Section>
            {isLoading ? (
              <MwMLoader category="general" />
            ) : data ? (
              <>
                <DataSection title="General">
                  <div className="global-data-container">
                    <AppLogo appLogoUrl={data.icon_url ?? ''} appName={data.display_name} size="medium" />
                    <div className="global-data-container__client-id-container">
                      <div>Client ID</div>
                      <div className="global-data-container__client-id-container__client-id">{data.client_id}</div>
                    </div>
                  </div>
                </DataSection>
                <Separator />
                <DataSection title="Google">
                  <DataTable>
                    <tr>
                      <td>Client ID (iOS)</td>
                      <td>{data.google_ios_client_id}</td>
                      <CopyCell valueToCopy={data.apple_ios_client_id} />
                    </tr>
                    <tr>
                      <td>Client ID (other)</td>
                      <td>{data.google_other_client_id}</td>
                      <CopyCell valueToCopy={data.google_other_client_id} />
                    </tr>
                    <tr>
                      <td>Secret</td>
                      <SecretTableCell hasSecret={!!data.google_client_secret} />
                    </tr>
                  </DataTable>
                </DataSection>
                <Separator />
                <DataSection title="Apple">
                  <DataTable>
                    <tr>
                      <td>Client ID (iOS)</td>
                      <td>{data.apple_ios_client_id}</td>
                      <CopyCell valueToCopy={data.apple_ios_client_id} />
                    </tr>
                    <tr>
                      <td>Client ID (other)</td>
                      <td>{data.apple_other_client_id}</td>
                      <CopyCell valueToCopy={data.apple_other_client_id} />
                    </tr>
                    <tr>
                      <td>Secret</td>
                      <SecretTableCell hasSecret={!!data.apple_client_secret} />
                    </tr>
                  </DataTable>
                </DataSection>
                <Separator />
                <DataSection title="Facebook">
                  <DataTable>
                    <tr>
                      <td>Client ID</td>
                      <td>{data.facebook_client_id}</td>
                      <CopyCell valueToCopy={data.facebook_client_id} />
                    </tr>
                    <tr>
                      <td>Secret</td>
                      <SecretTableCell hasSecret={!!data.facebook_client_secret} />
                    </tr>
                  </DataTable>
                </DataSection>
              </>
            ) : (
              <div>No data found for this app</div>
            )}
          </Section>
        </ContentLayout>
      </Layout>
    </>
  )
}

export default AppConfig
